import React, { useState, useEffect, useCallback } from 'react';
import { X, Trash, Clock, CaretDown, CaretUp, Warning } from '@phosphor-icons/react';
import { EpLabel } from './Shared';
import { COURSE_OPTIONS } from './constants';

export function ConflictResolutionModal({ conflict, onChoose, onCancel }) {
  const dates = conflict?.conflict_dates || [];
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onCancel}>
      <div className="bg-neutral-900 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-5"
        onClick={e => e.stopPropagation()} data-testid="conflict-modal">
        <div className="flex items-center gap-2 mb-3">
          <Warning size={20} weight="fill" className="text-amber-400" />
          <h3 className="text-sm font-bold text-white">Plans already exist</h3>
        </div>
        <p className="text-[12px] text-neutral-300 mb-3">
          {dates.length} day(s) in this range already have a plan saved.
        </p>
        <div className="rounded-lg p-2 mb-4 max-h-32 overflow-y-auto" style={{ background: 'rgba(255,255,255,0.04)' }}>
          <p className="text-[10px] text-neutral-400 flex flex-wrap gap-1.5">
            {dates.map(d => <span key={d} className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300">{d}</span>)}
          </p>
        </div>
        <p className="text-[11px] text-neutral-400 mb-3">What do you want to do?</p>
        <div className="space-y-2">
          <button onClick={() => onChoose('replace')} data-testid="conflict-replace-btn"
            className="w-full px-4 py-2.5 rounded-xl bg-[#FF6B35] text-white text-xs font-bold hover:opacity-90 text-left">
            <span className="block">Replace existing plans</span>
            <span className="block text-[10px] opacity-80 mt-0.5">Overwrites those days with fresh AI suggestions</span>
          </button>
          <button onClick={() => onChoose('skip')} data-testid="conflict-skip-btn"
            className="w-full px-4 py-2.5 rounded-xl bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 text-xs font-bold hover:bg-emerald-500/25 text-left">
            <span className="block">Skip existing days</span>
            <span className="block text-[10px] opacity-80 mt-0.5">Only fills the empty days, keeps your saved ones</span>
          </button>
          <button onClick={onCancel} data-testid="conflict-cancel-btn"
            className="w-full px-4 py-2.5 rounded-xl bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OccasionMenusSection({ api, eaterProfiles, onSeedDefaults, briefGuestSeed, clearBriefGuestSeed }) {
  const [openMode, setOpenMode] = useState(null);
  const [seed, setSeed] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [initialGuestId, setInitialGuestId] = useState(null);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await api.get(`/meals/occasions?archived=${showArchived ? 'true' : 'false'}`);
      setHistory(r.data || []);
    } catch (_) { /* ignore */ }
    setHistoryLoading(false);
  }, [api, showArchived]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  useEffect(() => {
    if (briefGuestSeed?.guest_id) {
      setSeed(null);
      setInitialGuestId(briefGuestSeed.guest_id);
      setOpenMode('guest');
      clearBriefGuestSeed && clearBriefGuestSeed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [briefGuestSeed]);

  const handleReuse = (occ) => {
    setSeed(occ);
    setOpenMode(occ.occasion_type === 'guest' ? 'guest' : 'party');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this saved menu?')) return;
    try {
      await api.delete(`/meals/occasions/${id}`);
      setHistory(h => h.filter(x => x.occasion_id !== id));
    } catch (_) { /* ignore */ }
  };

  const handleArchiveToggle = async (id, archived) => {
    try {
      await api.patch(`/meals/occasions/${id}`, { archived });
      setHistory(h => h.filter(x => x.occasion_id !== id));
    } catch (_) { /* ignore */ }
  };

  const handleBulk = async (action, ids) => {
    if (!ids || ids.length === 0) return;
    if (action === 'delete' && !window.confirm(`Delete ${ids.length} saved menu(s)?`)) return;
    try {
      await api.post('/meals/occasions/bulk', { action, occasion_ids: ids });
      setHistory(h => h.filter(x => !ids.includes(x.occasion_id)));
    } catch (_) { /* ignore */ }
  };

  const closeModal = () => { setOpenMode(null); setSeed(null); setInitialGuestId(null); };

  return (
    <div className="space-y-3" data-testid="occasion-menus-section">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <OccasionCard
          accent="#A855F7"
          emoji="🎉"
          title="Plan a Party Menu"
          subtitle="3-course feast that satisfies everyone — kids, adults, fussy uncles."
          testId="occasion-card-party"
          onClick={() => { setSeed(null); setInitialGuestId(null); setOpenMode('party'); }}
        />
        <OccasionCard
          accent="#FF6B35"
          emoji="🍽️"
          title="Plan a Guest Menu"
          subtitle="Special dinner/lunch for visitors — picks dishes around their taste."
          testId="occasion-card-guest"
          onClick={() => { setSeed(null); setInitialGuestId(null); setOpenMode('guest'); }}
        />
      </div>

      <OccasionHistoryDrawer
        history={history}
        loading={historyLoading}
        showArchived={showArchived}
        onToggleArchivedView={() => setShowArchived(v => !v)}
        onReuse={handleReuse}
        onDelete={handleDelete}
        onArchiveToggle={handleArchiveToggle}
        onBulk={handleBulk}
      />

      {openMode && (
        <OccasionBuilderModal
          api={api}
          mode={openMode}
          seed={seed}
          initialGuestId={initialGuestId}
          eaterProfiles={eaterProfiles || []}
          onSeedDefaults={onSeedDefaults}
          onSaved={fetchHistory}
          onClose={closeModal}
        />
      )}
    </div>
  );
}

function OccasionCard({ accent, emoji, title, subtitle, testId, onClick }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="text-left rounded-2xl p-5 transition-all hover:scale-[1.01] active:scale-[0.99] group"
      style={{
        background: `linear-gradient(135deg, ${accent}25, ${accent}08)`,
        border: `1px solid ${accent}40`,
      }}>
      <div className="flex items-start gap-3">
        <div className="text-3xl shrink-0 transition-transform group-hover:scale-110">{emoji}</div>
        <div className="min-w-0">
          <h4 className="text-sm sm:text-base font-bold text-white">{title}</h4>
          <p className="text-[11px] sm:text-xs mt-1" style={{ color: `${accent}` }}>{subtitle}</p>
          <p className="text-[10px] uppercase tracking-wider font-bold mt-3" style={{ color: accent }}>
            Tap to build →
          </p>
        </div>
      </div>
    </button>
  );
}

function OccasionHistoryDrawer({ history, loading, showArchived, onToggleArchivedView, onReuse, onDelete, onArchiveToggle, onBulk }) {
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const labelMap = Object.fromEntries(COURSE_OPTIONS.map(c => [c.key, c]));

  const sendToCook = (msg) => {
    if (!msg) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const dishCount = (occ) => Object.values(occ.courses || {}).reduce((n, arr) => n + (arr?.length || 0), 0);
  const items = (history || []).slice(0, 10);

  useEffect(() => {
    setSelected(prev => {
      const visibleIds = new Set(items.map(i => i.occasion_id));
      const next = new Set(Array.from(prev).filter(id => visibleIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [items]);

  const toggleSelected = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const allSelected = items.length > 0 && items.every(i => selected.has(i.occasion_id));
  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(items.map(i => i.occasion_id)));
  };
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    await onBulk(action, ids);
    setSelected(new Set());
  };

  if (!loading && items.length === 0 && !showArchived) {
    return null;
  }

  return (
    <div className="rounded-2xl overflow-hidden" data-testid="occasion-history-drawer"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="flex items-center gap-2 px-4 py-3">
        <button onClick={() => setOpen(o => !o)} data-testid="occasion-history-toggle"
          className="flex items-center gap-2 min-w-0 flex-1 text-left hover:opacity-80">
          <Clock size={14} weight="bold" className="text-neutral-400" />
          <span className="text-xs font-bold text-neutral-300">
            {showArchived ? 'Archived occasions' : 'Occasion history'}
          </span>
          <span className="text-[10px] text-neutral-500">{loading ? '…' : `${items.length} ${showArchived ? 'archived' : 'saved'}`}</span>
          {open ? <CaretUp size={12} weight="bold" className="text-neutral-400" /> : <CaretDown size={12} weight="bold" className="text-neutral-400" />}
        </button>
        <button onClick={onToggleArchivedView} data-testid="occasion-history-toggle-archived"
          className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${showArchived
            ? 'bg-amber-500/15 text-amber-300 border-amber-500/40'
            : 'bg-white/5 text-neutral-400 border-white/10 hover:text-white'}`}>
          {showArchived ? '← Active' : 'Show archived'}
        </button>
      </div>

      {open && selected.size > 0 && (
        <div className="px-4 py-2 border-t border-white/5 flex items-center justify-between gap-2 flex-wrap"
          style={{ background: 'rgba(168,85,247,0.08)' }} data-testid="occasion-bulk-bar">
          <p className="text-[11px] font-bold text-purple-200">{selected.size} selected</p>
          <div className="flex flex-wrap gap-1.5">
            {!showArchived && (
              <button onClick={() => runBulk('archive')} data-testid="occasion-bulk-archive"
                className="px-2.5 py-1 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[10px] font-bold hover:bg-amber-500/25">
                Archive
              </button>
            )}
            {showArchived && (
              <button onClick={() => runBulk('unarchive')} data-testid="occasion-bulk-unarchive"
                className="px-2.5 py-1 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[10px] font-bold hover:bg-emerald-500/25">
                Unarchive
              </button>
            )}
            <button onClick={() => runBulk('delete')} data-testid="occasion-bulk-delete"
              className="px-2.5 py-1 rounded-lg bg-red-500/15 text-red-300 border border-red-500/30 text-[10px] font-bold hover:bg-red-500/25">
              Delete
            </button>
            <button onClick={clearSelection} data-testid="occasion-bulk-clear"
              className="px-2.5 py-1 rounded-lg bg-white/5 text-neutral-300 border border-white/10 text-[10px] font-bold hover:bg-white/10">
              Clear
            </button>
          </div>
        </div>
      )}

      {open && (
        <div className="border-t border-white/5 divide-y divide-white/5">
          {loading && <div className="p-4 text-[11px] text-neutral-500">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="p-6 text-center text-[11px] text-neutral-500">No archived occasions.</div>
          )}
          {!loading && items.length > 0 && (
            <label className="flex items-center gap-2 px-4 py-2 hover:bg-white/5 cursor-pointer" data-testid="occasion-history-select-all">
              <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="accent-purple-500" />
              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">
                {allSelected ? 'Deselect all' : 'Select all'}
              </span>
            </label>
          )}
          {!loading && items.map(occ => {
            const isExp = expandedId === occ.occasion_id;
            const isSel = selected.has(occ.occasion_id);
            const accent = occ.occasion_type === 'guest' ? '#FF6B35' : '#A855F7';
            const emoji = occ.occasion_type === 'guest' ? '🍽️' : '🎉';
            return (
              <div key={occ.occasion_id} className={`px-4 py-3 ${isSel ? 'bg-purple-500/5' : ''}`}
                data-testid={`occasion-history-row-${occ.occasion_id}`}>
                <div className="flex items-start gap-3">
                  <input type="checkbox" checked={isSel} onChange={() => toggleSelected(occ.occasion_id)}
                    data-testid={`occasion-history-select-${occ.occasion_id}`}
                    className="mt-1 accent-purple-500 shrink-0" />
                  <div className="text-xl shrink-0">{emoji}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <p className="text-sm font-bold text-white truncate">{occ.title || (occ.occasion_type === 'guest' ? 'Guest Menu' : 'Party Menu')}</p>
                      <span className="text-[10px] uppercase font-bold" style={{ color: accent }}>{occ.occasion_type}</span>
                      {occ.archived && <span className="text-[9px] uppercase font-bold text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded">Archived</span>}
                    </div>
                    <p className="text-[10px] text-neutral-500 mt-0.5">
                      {occ.guest_count} guests · {occ.meal_type}
                      {occ.occasion_date ? ` · ${occ.occasion_date}` : ''}
                      {' · '}
                      {dishCount(occ)} dish{dishCount(occ) === 1 ? '' : 'es'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1 shrink-0">
                    <button onClick={() => setExpandedId(isExp ? null : occ.occasion_id)}
                      data-testid={`occasion-history-expand-${occ.occasion_id}`}
                      className="px-2 py-1 rounded-lg bg-white/5 text-neutral-300 text-[10px] font-bold hover:bg-white/10">
                      {isExp ? 'Hide' : 'View'}
                    </button>
                    <button onClick={() => sendToCook(occ.whatsapp_message)}
                      data-testid={`occasion-history-send-${occ.occasion_id}`}
                      className="px-2 py-1 rounded-lg text-white text-[10px] font-bold"
                      style={{ background: '#25D366' }} title="Send WhatsApp message to cook">
                      Send to cook
                    </button>
                    <button onClick={() => onReuse(occ)}
                      data-testid={`occasion-history-reuse-${occ.occasion_id}`}
                      className="px-2 py-1 rounded-lg text-white text-[10px] font-bold"
                      style={{ background: accent }} title="Re-generate fresh dishes with same eaters">
                      Reuse
                    </button>
                    <button onClick={() => onArchiveToggle(occ.occasion_id, !occ.archived)}
                      data-testid={`occasion-history-archive-${occ.occasion_id}`}
                      className="px-2 py-1 rounded-lg bg-amber-500/10 text-amber-300 text-[10px] font-bold hover:bg-amber-500/20"
                      title={occ.archived ? 'Unarchive' : 'Archive'}>
                      {occ.archived ? 'Unarchive' : 'Archive'}
                    </button>
                    <button onClick={() => onDelete(occ.occasion_id)}
                      data-testid={`occasion-history-delete-${occ.occasion_id}`}
                      className="p-1 rounded-lg bg-red-500/10 text-red-300 hover:bg-red-500/20" title="Delete">
                      <Trash size={11} />
                    </button>
                  </div>
                </div>

                {isExp && (
                  <div className="mt-3 pl-9 space-y-2" data-testid={`occasion-history-detail-${occ.occasion_id}`}>
                    {Object.entries(occ.courses || {}).map(([k, list]) => (
                      <div key={k}>
                        <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">
                          {labelMap[k]?.icon} {labelMap[k]?.label || k}
                        </p>
                        <ul className="mt-1 space-y-0.5">
                          {list.map((d, i) => (
                            <li key={i} className="text-[11px] text-neutral-300">
                              <span className="font-semibold">{d.dish}</span>
                              {d.serves ? <span className="text-neutral-500"> · {d.serves}</span> : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                    {occ.whatsapp_message && (
                      <div className="rounded-lg p-2.5 text-[11px] text-neutral-300 whitespace-pre-wrap mt-2"
                        style={{ background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.20)' }}>
                        {occ.whatsapp_message}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OccasionBuilderModal({ api, mode, seed, initialGuestId, eaterProfiles, onSeedDefaults, onSaved, onClose }) {
  const isParty = mode === 'party';
  const defaultCourses = isParty
    ? ['starters', 'mains', 'breads', 'desserts']
    : ['mains', 'sides', 'breads'];
  const seedCourses = seed?.course_keys && seed.course_keys.length > 0 ? seed.course_keys : null;
  const seedProfileIds = Array.isArray(seed?.profile_ids) ? seed.profile_ids : null;
  const [form, setForm] = useState({
    title: seed?.title || '',
    meal_type: seed?.meal_type || 'dinner',
    guest_count: seed?.guest_count ?? (isParty ? 12 : 4),
    occasion_date: '',
    courses: new Set(seedCourses || defaultCourses),
    profile_ids: new Set(seedProfileIds || (eaterProfiles || []).filter(p => p.active !== false).map(p => p.profile_id)),
    extra_notes: seed?.extra_notes || '',
  });
  const [adhoc, setAdhoc] = useState(seed?.adhoc_guests || []);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [remembered, setRemembered] = useState([]);
  const [pickedGuestIds, setPickedGuestIds] = useState(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/meals/remembered-guests');
        if (!cancelled) setRemembered(r.data || []);
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [api]);

  // When the modal is opened from an AI Chef Brief action, auto-pick the seeded guest
  useEffect(() => {
    if (!initialGuestId || remembered.length === 0) return;
    if (pickedGuestIds.has(initialGuestId)) return;
    const g = remembered.find(x => x.guest_id === initialGuestId);
    if (!g) return;
    toggleRemembered(g);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialGuestId, remembered]);

  const toggleRemembered = (g) => {
    const wasPicked = pickedGuestIds.has(g.guest_id);
    setPickedGuestIds(prev => {
      const next = new Set(prev);
      if (next.has(g.guest_id)) next.delete(g.guest_id);
      else next.add(g.guest_id);
      return next;
    });
    if (wasPicked) {
      setAdhoc(a => a.filter(x => x.__from_guest_id !== g.guest_id));
    } else {
      setAdhoc(a => {
        if (a.some(x => x.__from_guest_id === g.guest_id)) return a;  // guard against StrictMode double-invoke
        return [
          ...a,
          {
            __from_guest_id: g.guest_id,
            name: g.name,
            prefs: g.dietary_notes || '',
            restrictions: g.restrictions || '',
          },
        ];
      });
    }
  };

  const toggleCourse = (k) => {
    setForm(f => {
      const s = new Set(f.courses);
      if (s.has(k)) s.delete(k); else s.add(k);
      return { ...f, courses: s };
    });
  };
  const toggleProfile = (id) => {
    setForm(f => {
      const s = new Set(f.profile_ids);
      if (s.has(id)) s.delete(id); else s.add(id);
      return { ...f, profile_ids: s };
    });
  };
  const addAdhoc = () => setAdhoc(a => [...a, { name: '', prefs: '', restrictions: '' }]);
  const updateAdhoc = (i, k, v) => setAdhoc(a => a.map((g, idx) => idx === i ? { ...g, [k]: v } : g));
  const removeAdhoc = (i) => setAdhoc(a => a.filter((_, idx) => idx !== i));

  const generate = async () => {
    setError(''); setResult(null); setLoading(true);
    try {
      const body = {
        occasion_type: mode,
        title: form.title,
        meal_type: form.meal_type,
        guest_count: Number(form.guest_count) || 1,
        occasion_date: form.occasion_date,
        courses: Array.from(form.courses),
        profile_ids: Array.from(form.profile_ids),
        adhoc_guests: adhoc
          .filter(g => (g.name || '').trim())
          .map(({ __from_guest_id, ...rest }) => rest),  // eslint-disable-line no-unused-vars
        extra_notes: form.extra_notes,
      };
      const res = await api.post('/meals/occasion-menu', body);
      setResult(res.data);
      if (res.data?.saved && onSaved) onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      const safe = /openai|authentication|api key|quota|rate/i.test(detail)
        ? detail
        : 'Generation failed. Please retry.';
      setError(safe);
    }
    setLoading(false);
  };

  const copyWhatsApp = () => {
    const msg = result?.whatsapp_message || '';
    if (!msg) return;
    navigator.clipboard.writeText(msg).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const sendWhatsApp = () => {
    const msg = result?.whatsapp_message || '';
    if (!msg) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const accent = isParty ? '#A855F7' : '#FF6B35';

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="bg-neutral-900 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()} data-testid="occasion-modal">
        <div className="sticky top-0 z-10 px-5 py-3 border-b border-white/10 bg-neutral-900 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="text-xl">{isParty ? '🎉' : '🍽️'}</div>
            <h3 className="text-sm font-bold text-white truncate">{isParty ? 'Plan a Party Menu' : 'Plan a Guest Menu'}</h3>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white" data-testid="occasion-close-btn">
            <X size={18} />
          </button>
        </div>

        {!result && (
          <div className="p-5 space-y-4">
            {error && <div className="text-xs text-red-300 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">{error}</div>}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <EpLabel>Title (optional)</EpLabel>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  data-testid="occasion-title" className="ep-input" placeholder={isParty ? 'Diwali Bash' : 'Sharma uncle visit'} />
              </div>
              <div>
                <EpLabel>Date (optional)</EpLabel>
                <input type="date" value={form.occasion_date} onChange={e => setForm(f => ({ ...f, occasion_date: e.target.value }))}
                  data-testid="occasion-date" className="ep-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <EpLabel>Meal type</EpLabel>
                <select value={form.meal_type} onChange={e => setForm(f => ({ ...f, meal_type: e.target.value }))}
                  data-testid="occasion-meal-type" className="ep-input">
                  <option value="breakfast" style={{ background: '#0a0a0a' }}>Breakfast</option>
                  <option value="lunch" style={{ background: '#0a0a0a' }}>Lunch</option>
                  <option value="tea" style={{ background: '#0a0a0a' }}>Tea / High tea</option>
                  <option value="dinner" style={{ background: '#0a0a0a' }}>Dinner</option>
                </select>
              </div>
              <div>
                <EpLabel>Guest count ({form.guest_count})</EpLabel>
                <input type="number" min="1" max="500" value={form.guest_count}
                  onChange={e => setForm(f => ({ ...f, guest_count: e.target.value }))}
                  data-testid="occasion-guest-count" className="ep-input" />
              </div>
            </div>

            <div>
              <EpLabel>Courses to include</EpLabel>
              <div className="flex flex-wrap gap-1.5" data-testid="occasion-course-chips">
                {COURSE_OPTIONS.map(c => {
                  const on = form.courses.has(c.key);
                  return (
                    <button key={c.key} type="button" onClick={() => toggleCourse(c.key)}
                      data-testid={`occasion-course-${c.key}`}
                      className={`px-3 py-1.5 rounded-full text-[11px] font-bold border transition-all ${on
                        ? 'border-purple-500/60 text-purple-200'
                        : 'border-white/10 text-neutral-400 hover:border-white/30'}`}
                      style={on ? { background: `${accent}25` } : {}}>
                      {c.icon} {c.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {eaterProfiles.length === 0 ? (
              <div className="rounded-xl p-3 text-[11px] text-neutral-400" style={{ background: 'rgba(168,85,247,0.05)', border: '1px solid rgba(168,85,247,0.15)' }}>
                💡 Add Eater Profiles (Kids / Adults / per-person) for sharper recommendations.{' '}
                <button onClick={onSeedDefaults} data-testid="occasion-seed-defaults"
                  className="text-purple-300 underline font-bold">Quick add</button>
              </div>
            ) : (
              <div>
                <EpLabel>Family eaters (we'll respect their tastes)</EpLabel>
                <div className="flex flex-wrap gap-1.5" data-testid="occasion-profile-chips">
                  {eaterProfiles.map(p => {
                    const on = form.profile_ids.has(p.profile_id);
                    return (
                      <button key={p.profile_id} type="button" onClick={() => toggleProfile(p.profile_id)}
                        data-testid={`occasion-profile-${p.profile_id}`}
                        className={`px-3 py-1.5 rounded-full text-[11px] font-bold border ${on
                          ? 'bg-purple-500/15 text-purple-200 border-purple-500/40'
                          : 'bg-white/5 text-neutral-400 border-white/10'}`}>
                        {p.icon || '🧑'} {p.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-1">
                <EpLabel>Ad-hoc guests (one-time)</EpLabel>
                <button onClick={addAdhoc} data-testid="occasion-add-guest"
                  className="text-[10px] font-bold px-2 py-1 rounded-lg bg-purple-500/15 text-purple-300 border border-purple-500/30">
                  + Add guest
                </button>
              </div>

              {remembered.length > 0 && (
                <div className="mb-2 rounded-lg p-2"
                  style={{ background: 'rgba(236,72,153,0.06)', border: '1px solid rgba(236,72,153,0.20)' }}
                  data-testid="occasion-remembered-picker">
                  <p className="text-[9px] uppercase tracking-wider font-bold text-pink-300 mb-1.5">Remembered guests · tap to add</p>
                  <div className="flex flex-wrap gap-1">
                    {remembered.map(g => {
                      const on = pickedGuestIds.has(g.guest_id);
                      return (
                        <button key={g.guest_id} type="button" onClick={() => toggleRemembered(g)}
                          data-testid={`occasion-remembered-${g.guest_id}`}
                          title={[g.dietary_notes, g.restrictions ? `Avoid: ${g.restrictions}` : ''].filter(Boolean).join(' · ')}
                          className={`px-2 py-1 rounded-full text-[10px] font-bold border transition-colors ${on
                            ? 'bg-pink-500/20 text-pink-200 border-pink-500/50'
                            : 'bg-white/5 text-neutral-300 border-white/10 hover:bg-pink-500/10 hover:text-pink-200 hover:border-pink-500/40'}`}>
                          {on ? '✓ ' : '+ '}{g.name}
                          {g.visit_count > 0 && <span className="text-[8px] opacity-70 ml-1">×{g.visit_count}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {adhoc.length === 0 ? (
                <p className="text-[10px] text-neutral-500 italic">e.g., &quot;Sharma uncle — Jain, no garlic&quot;</p>
              ) : (
                <div className="space-y-2">
                  {adhoc.map((g, i) => (
                    <div key={i} className="grid grid-cols-12 gap-1.5" data-testid={`occasion-guest-row-${i}`}>
                      <input value={g.name} onChange={e => updateAdhoc(i, 'name', e.target.value)}
                        placeholder="Name" className="ep-input col-span-3" />
                      <input value={g.prefs} onChange={e => updateAdhoc(i, 'prefs', e.target.value)}
                        placeholder="Likes (paneer, sweet…)" className="ep-input col-span-4" />
                      <input value={g.restrictions} onChange={e => updateAdhoc(i, 'restrictions', e.target.value)}
                        placeholder="Avoid (garlic, eggs…)" className="ep-input col-span-4" />
                      <button onClick={() => removeAdhoc(i)} className="col-span-1 rounded-lg bg-red-500/10 text-red-300 hover:bg-red-500/20 text-xs">✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <EpLabel>Extra notes for AI Chef</EpLabel>
              <textarea rows="2" value={form.extra_notes} onChange={e => setForm(f => ({ ...f, extra_notes: e.target.value }))}
                data-testid="occasion-extra-notes" className="ep-input"
                placeholder={isParty ? 'e.g., birthday, theme cocktail, kids will play in garden' : 'e.g., guest stays till 10pm, no alcohol'} />
            </div>
          </div>
        )}

        {result && (
          <OccasionResultView result={result} accent={accent} api={api} adhoc={adhoc} onBack={() => setResult(null)} onCopy={copyWhatsApp} copied={copied} onSend={sendWhatsApp} />
        )}

        {!result && (
          <div className="sticky bottom-0 px-5 py-3 border-t border-white/10 bg-neutral-900 flex justify-end gap-2">
            <button onClick={onClose} disabled={loading} className="px-3 py-2 rounded-lg bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">Cancel</button>
            <button onClick={generate} disabled={loading || form.courses.size === 0} data-testid="occasion-generate"
              className="px-4 py-2 rounded-lg text-white text-xs font-bold disabled:opacity-50"
              style={{ background: loading ? '#666' : accent }}>
              {loading ? 'Cooking up ideas…' : '✨ Generate menu'}
            </button>
          </div>
        )}

        <style>{`.ep-input{width:100%;padding:8px 12px;border-radius:10px;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.10);font-size:12px;color:#fff;outline:none}.ep-input:focus{border-color:${accent}}`}</style>
      </div>
    </div>
  );
}

function OccasionResultView({ result, accent, api, adhoc, onBack, onCopy, copied, onSend }) {
  const courses = result?.courses || {};
  const order = ['starters', 'mains', 'sides', 'breads', 'rice', 'desserts', 'drinks'];
  const keys = order.filter(k => courses[k] && courses[k].length > 0);
  const labelMap = Object.fromEntries(COURSE_OPTIONS.map(c => [c.key, c]));

  // "Remember these guests" — show only for guests that came from raw ad-hoc entries
  // (i.e., the user typed them in this session, not picked from an existing remembered guest).
  const unsavedNamedGuests = (adhoc || [])
    .filter(g => (g?.name || '').trim() && !g.__from_guest_id)
    .map(g => g.name.trim());
  const [rememberBusy, setRememberBusy] = useState(false);
  const [rememberDone, setRememberDone] = useState(null);
  const [rememberErr, setRememberErr] = useState('');
  const [dismissed, setDismissed] = useState(false);

  const handleRemember = async () => {
    if (!result?.occasion_id || unsavedNamedGuests.length === 0) return;
    setRememberBusy(true); setRememberErr('');
    try {
      const r = await api.post('/meals/remembered-guests/from-occasion', {
        occasion_id: result.occasion_id,
        guest_names: unsavedNamedGuests,
      });
      setRememberDone(r.data);
    } catch (e) {
      setRememberErr(e?.response?.data?.detail || 'Could not save guests.');
    }
    setRememberBusy(false);
  };

  return (
    <div className="p-5 space-y-4" data-testid="occasion-result">
      <div className="rounded-xl p-3" style={{ background: `${accent}15`, border: `1px solid ${accent}40` }}>
        <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: accent }}>
          {result.guest_count} guests · {result.meal_type}{result.occasion_date ? ` · ${result.occasion_date}` : ''}
        </p>
        <p className="text-sm font-bold text-white">{result.title || 'Menu'}</p>
      </div>

      {keys.length === 0 && (
        <div className="rounded-lg p-3 text-[11px] text-neutral-300" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {result.raw_suggestion ? (
            <>
              <p className="text-amber-300 font-bold mb-1.5">AI returned suggestions but I could not auto-parse them. Raw output:</p>
              <pre className="whitespace-pre-wrap text-[10px]" data-testid="occasion-raw-suggestion">{result.raw_suggestion}</pre>
            </>
          ) : (
            <p>No dishes returned. Try with different courses or extra notes.</p>
          )}
        </div>
      )}

      {keys.map(k => (
        <div key={k}>
          <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-2">
            {labelMap[k]?.icon} {labelMap[k]?.label || k}
          </p>
          <div className="space-y-1.5" data-testid={`occasion-course-${k}-result`}>
            {courses[k].map((d, i) => (
              <div key={i} className="rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <p className="text-sm font-semibold text-white">{d.dish}</p>
                {d.serves && <p className="text-[10px] text-neutral-500 mt-0.5">{d.serves}</p>}
                {d.tip && <p className="text-[10px] mt-1" style={{ color: accent }}>💡 {d.tip}</p>}
              </div>
            ))}
          </div>
        </div>
      ))}

      {!dismissed && unsavedNamedGuests.length > 0 && result?.occasion_id && (
        <div className="rounded-xl p-3" data-testid="occasion-remember-banner"
          style={{ background: 'linear-gradient(135deg, rgba(236,72,153,0.12), rgba(168,85,247,0.05))', border: '1px solid rgba(236,72,153,0.30)' }}>
          {!rememberDone ? (
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-[180px]">
                <p className="text-[11px] font-bold text-pink-300 uppercase tracking-wider mb-0.5">Remember these guests?</p>
                <p className="text-[11px] text-neutral-300">
                  Save <b>{unsavedNamedGuests.join(', ')}</b> for next time — AI will pre-fill their preferences and these {keys.length > 0 ? 'dishes will be added to their favourites' : 'guests are stored for one-tap reuse'}.
                </p>
                {rememberErr && <p className="text-[10px] text-red-300 mt-1">{rememberErr}</p>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setDismissed(true)} disabled={rememberBusy} data-testid="occasion-remember-dismiss"
                  className="px-3 py-1.5 rounded-lg bg-white/5 text-neutral-300 border border-white/10 text-[11px] font-bold hover:bg-white/10">
                  Not now
                </button>
                <button onClick={handleRemember} disabled={rememberBusy} data-testid="occasion-remember-btn"
                  className="px-3 py-1.5 rounded-lg bg-pink-500 text-white text-[11px] font-bold hover:bg-pink-400 disabled:opacity-60 flex items-center gap-1.5">
                  {rememberBusy ? (
                    <>
                      <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>✨ Remember them</>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-[12px] text-emerald-300 font-semibold" data-testid="occasion-remember-done">
              ✓ Saved {(rememberDone.saved_count || 0) + (rememberDone.updated_count || 0)} guest{((rememberDone.saved_count || 0) + (rememberDone.updated_count || 0)) === 1 ? '' : 's'}
              {rememberDone.dishes_remembered > 0 ? ` · ${rememberDone.dishes_remembered} dish${rememberDone.dishes_remembered === 1 ? '' : 'es'} added to their favourites` : ''}.
              Find them in <b>Eaters → Remembered Guests</b>.
            </p>
          )}
        </div>
      )}

      {result.whatsapp_message && (
        <div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1">WhatsApp message for the cook</p>
          <div className="rounded-lg p-3 text-[11px] text-neutral-300 whitespace-pre-wrap"
            style={{ background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.20)' }}
            data-testid="occasion-whatsapp-msg">
            {result.whatsapp_message}
          </div>
        </div>
      )}

      <div className="sticky bottom-0 -mx-5 px-5 py-3 mt-2 border-t border-white/10 bg-neutral-900 flex flex-wrap gap-2 justify-end">
        <button onClick={onBack} data-testid="occasion-back-btn"
          className="px-3 py-2 rounded-lg bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">← Edit</button>
        <button onClick={onCopy} data-testid="occasion-copy-btn"
          className="px-3 py-2 rounded-lg bg-white/10 text-white text-xs font-bold hover:bg-white/15">
          {copied ? '✓ Copied' : 'Copy message'}
        </button>
        <button onClick={onSend} data-testid="occasion-whatsapp-btn"
          className="px-3 py-2 rounded-lg text-white text-xs font-bold"
          style={{ background: '#25D366' }}>
          Send via WhatsApp
        </button>
      </div>
    </div>
  );
}
